import React from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';

import { formatLumberGrade, formatLumberSpecies, Lumber, LumberWithStockLengths } from '@paragon/contract-library';

import { makeLumberTreatmentTypeHumanReadable, stockLengthsToString } from '../../util';

const formatDimensions = (lumber: Lumber) => `${lumber.nominalThickness}x${lumber.nominalWidth}`;

const formatCellDimensions = (row: LumberWithStockLengths) => formatDimensions(row.lumber);

export const formatLumber = (lumber: Lumber) =>
  `${formatLumberSpecies(lumber.species, lumber.tangibleSpecies)} ${formatLumberGrade(lumber.grade, lumber.tangibleGrades)} ${formatDimensions(lumber)}`;

interface Column {
  readonly name: string;
  readonly title: string;
  readonly width?: number;
  readonly getCellValue: (row: LumberWithStockLengths) => string;
}

const columns: readonly Column[] = [
  {
    name: 'grade',
    title: 'Grade',
    width: 130,
    getCellValue: (row: LumberWithStockLengths) => formatLumberGrade(row.lumber.grade, row.lumber.tangibleGrades),
  },
  {
    name: 'species',
    title: 'Species',
    width: 70,
    getCellValue: (row: LumberWithStockLengths) => formatLumberSpecies(row.lumber.species, row.lumber.tangibleSpecies),
  },
  { name: 'dimensions', title: 'Size', width: 70, getCellValue: formatCellDimensions },
  {
    name: 'treatmentType',
    title: 'Treatment Type',
    width: 120,
    getCellValue: (row: LumberWithStockLengths) => makeLumberTreatmentTypeHumanReadable(row.lumber.treatmentType),
  },
  {
    name: 'stockLengths',
    title: 'Stock Lengths',
    getCellValue: (row: LumberWithStockLengths) => stockLengthsToString(row.stockLengths),
  },
];

interface Props {
  readonly title: string;
  readonly flavorText: string;
  readonly lumbers: readonly LumberWithStockLengths[] | undefined;
}

export function LumberListPreview(props: Props) {
  return (
    <>
      <Typography variant="h4" gutterBottom>
        {props.title}
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        {props.flavorText}
      </Typography>

      <Paper elevation={0} sx={{ height: '600px' }}>
        <TableVirtuoso
          data={props.lumbers ?? []}
          components={VirtuosoTableComponents}
          fixedHeaderContent={() => (
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  size="small"
                  sx={{ width: column.width === undefined ? undefined : column.width }}
                  key={column.name}
                >
                  {column.title}
                </TableCell>
              ))}
            </TableRow>
          )}
          itemContent={(_index: number, lumberWithStockLengths: LumberWithStockLengths) => (
            <>
              {columns.map((column) => (
                <TableCell size="small" key={column.name}>
                  {column.getCellValue(lumberWithStockLengths)}
                </TableCell>
              ))}
            </>
          )}
        />
      </Paper>
    </>
  );
}

const VirtuosoTableComponents: TableComponents<LumberWithStockLengths> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => <TableContainer {...props} ref={ref} />),
  Table: (props) => <Table {...props} style={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />,
  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead {...props} ref={ref} sx={{ bgcolor: 'background.paper' }} />
  )),
  TableRow,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => <TableBody {...props} ref={ref} />),
};
